import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Card } from '~/eds/src/components/Card/Card';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';

export interface EditableTextFieldProps {
  title: string;
  subtitle: string;
  value: string;
  placeholder: string;
  isEditing: boolean;
  isSaving: boolean;
  maxLength?: number;
  rows?: number;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  showOptionalLabel?: boolean;
  optionalTooltip?: string;
  editButtonText?: string;
  saveButtonText?: string;
  cancelButtonText?: string;
}

const DEFAULT_MAX_LENGTH = 10000;
const DEFAULT_ROWS = 5;

/**
 * A reusable component for editable text fields with built-in edit/save/cancel functionality.
 * The component displays a text area with a title, subtitle, character count, and action buttons.
 * It supports two modes: view mode (with an Edit button) and edit mode (with Cancel and Save buttons).
 */
export const EditableTextField = ({
  title,
  subtitle,
  value,
  placeholder,
  isEditing,
  isSaving,
  maxLength = DEFAULT_MAX_LENGTH,
  rows = DEFAULT_ROWS,
  onEdit,
  onSave,
  onCancel,
  onChange,
  showOptionalLabel = true,
  optionalTooltip,
  editButtonText,
  saveButtonText,
  cancelButtonText,
}: EditableTextFieldProps) => {
  const intl = useIntl();

  const footerActions = useMemo(
    () =>
      !isEditing
        ? [
            {
              text:
                editButtonText ??
                intl.formatMessage({
                  id: 'EditableTextField.editButton',
                  defaultMessage: 'Edit',
                  description: 'Edit button text',
                }),
              onClick: onEdit,
              level: 'secondary' as const,
              disabled: isSaving,
            },
          ]
        : [
            {
              text:
                cancelButtonText ??
                intl.formatMessage({
                  id: 'EditableTextField.cancelButton',
                  defaultMessage: 'Cancel',
                  description: 'Cancel button text',
                }),
              onClick: onCancel,
              level: 'secondary' as const,
              disabled: isSaving,
            },
            {
              text:
                saveButtonText ??
                intl.formatMessage({
                  id: 'EditableTextField.saveButton',
                  defaultMessage: 'Save',
                  description: 'Save button text',
                }),
              onClick: onSave,
              level: 'primary' as const,
              disabled: isSaving,
              isLoading: isSaving,
            },
          ],
    [
      isEditing,
      isSaving,
      onEdit,
      onSave,
      onCancel,
      intl,
      editButtonText,
      saveButtonText,
      cancelButtonText,
    ],
  );

  const characterCountText = intl.formatMessage(
    {
      id: 'EditableTextField.characterCount',
      defaultMessage: '{count}/{max} characters',
      description: 'Character count display',
    },
    { count: value.length, max: maxLength },
  );

  return (
    <Card
      mode="bordered"
      size="m"
      footer={{
        leftContent: (
          <Text variant="tiny" color="text.secondary">
            {characterCountText}
          </Text>
        ),
        actions: footerActions,
      }}
    >
      <Layout direction="column" spacing={4}>
        <Layout direction="column" spacing={2}>
          <Layout direction="row" align="center" spacing={2}>
            <Text variant="body-bold">{title}</Text>
            {showOptionalLabel && (
              <Label variant="body" info={optionalTooltip} theme="light">
                {intl.formatMessage({
                  id: 'EditableTextField.optional',
                  defaultMessage: '(Optional)',
                  description: 'Optional label',
                })}
              </Label>
            )}
          </Layout>
          <Text variant="body" color="text.secondary">
            {subtitle}
          </Text>
        </Layout>
        <TextArea
          name={title}
          value={value}
          onChange={(newValue) => onChange(newValue || '')}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          readOnly={!isEditing}
          disabled={isSaving}
        />
      </Layout>
    </Card>
  );
};
